import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getProjectWiseCost } from "../../redux/adminUnitReportSlice/adminUnitReportSlice";
import { toNumber, makeProjectKey } from "./useUnitWiseCostData";

/* -------------------------------------------------------------------------- */
/*                                  HELPERS                                   */
/* -------------------------------------------------------------------------- */

// Reads a single unit-entry's value for the currently selected FY.
// ASSUMPTION: each project.units[] entry mirrors the project-level shape,
// i.e. { MNF_UNIT, UNIT_NAME, total, financial_years: { "<fy>": value } }.
// Adjust here if your API's unit-entry field names differ.
const getUnitEntryValue = (unitEntry, selectedFY) => {
    if (!selectedFY || selectedFY === "OVERALL") {
        return toNumber(unitEntry.total);
    }
    return toNumber(unitEntry.financial_years?.[selectedFY]);
};

/* -------------------------------------------------------------------------- */
/*                                    HOOK                                    */
/* -------------------------------------------------------------------------- */

/**
 * For each project, resolves either:
 *  - selectedUnit === "ALL": the single highest-value unit contributing to
 *    that project (which unit + how much), or
 *  - selectedUnit === "<MNF_UNIT>": that project's value in that one unit.
 * Projects with no match / zero value are dropped. Result is sorted
 * descending by value.
 *
 * Fully independent of any other chart - own local state, own fetch.
 */
export const useProjectWiseCostData = ({
    costLevel,
    valueMode,
    selectedFY,
    selectedProjectKeys,
    projectKeyToProject,
    selectedUnit, // "ALL" or a MNF_UNIT code
}) => {

    const dispatch = useDispatch();

    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const requestIdRef = useRef(0);
    const projectKeyToProjectRef = useRef(projectKeyToProject);
    projectKeyToProjectRef.current = projectKeyToProject;

    useEffect(() => {

        // const projectsPayload = selectedProjectKeys
        //     .map((key) => projectKeyToProject.get(key))
        const projectsPayload = selectedProjectKeys
            .map((key) => projectKeyToProjectRef.current.get(key))
            .filter(Boolean)
            .map(({ IO_NO, REV_NO }) => ({ IO_NO, REV_NO }));

        const currentRequestId = ++requestIdRef.current;

        setLoading(true);
        setError(null);

        // NOTE: MNF_UNIT is intentionally NOT sent - we need every
        // project's full per-unit breakdown to determine the highest one
        // client-side. The "ALL vs specific unit" logic happens below,
        // not at the API layer.
        dispatch(
            getProjectWiseCost({
                PROJECT_TYPE: "ALL",
                COST_LEVEL: costLevel,
                VALUE_MODE: valueMode,
                ...(projectsPayload.length > 0 ? { projects: projectsPayload } : {}),
            })
        )
            .unwrap()
            .then((res) => {
                if (currentRequestId !== requestIdRef.current) return;
                setReportData(res?.data ?? res ?? null);
            })
            .catch((err) => {
                if (currentRequestId !== requestIdRef.current) return;
                setError(err);
                setReportData(null);
            })
            .finally(() => {
                if (currentRequestId !== requestIdRef.current) return;
                setLoading(false);
            });

    }, [
        dispatch,
        costLevel,
        valueMode,
        selectedProjectKeys.join(","),
    ]);

    const groups = reportData?.groups ?? [];
    const yearLabels = reportData?.labels ?? [];

    const MIN_FY_START_YEAR = 2026;

    const financialYearOptions = useMemo(() => {
        return [
            { label: "Overall", value: "OVERALL" },
            ...yearLabels
                .filter((fy) => Number(String(fy).split("-")[0]) >= MIN_FY_START_YEAR)
                .map((fy) => ({ label: fy, value: fy })),
        ];
    }, [yearLabels]);

    const rankedRows = useMemo(() => {

        const rows = [];

        groups.forEach((group) => {
            (group.projects || []).forEach((project) => {

                const unitsForProject = project.units || [];
                let barValue = 0;      // project-level total → what the bar's LENGTH represents
                let labelUnit = null;  // the highest (or selected) unit → what the LABEL shows

                if (!selectedUnit || selectedUnit === "ALL") {
                    // The project's own total/financial_years value is already the
                    // correct aggregate (API-computed) - unit-level totals are a
                    // different metric and must NOT be summed to derive it (summing
                    // them produced nonsensical bars going past 100%/multiple crore).
                    barValue = getUnitEntryValue(project, selectedFY);

                    unitsForProject.forEach((unitEntry) => {
                        const value = getUnitEntryValue(unitEntry, selectedFY);
                        if (!labelUnit || value > labelUnit.value) {
                            labelUnit = { unitEntry, value };            // highest-contributing unit, for the label only
                        }
                    });
                } else {
                    const match = unitsForProject.find(
                        (unitEntry) => unitEntry.MNF_UNIT === selectedUnit
                    );
                    if (match) {
                        const value = getUnitEntryValue(match, selectedFY);
                        barValue = value;
                        labelUnit = { unitEntry: match, value };
                    }
                }

                if (!labelUnit || barValue <= 0) return;

                rows.push({
                    projectKey: makeProjectKey(project.IO_NO, project.REV_NO),
                    ioNo: project.IO_NO,
                    projectName: project.PROJ_NAME,
                    unitCode: labelUnit.unitEntry.MNF_UNIT,
                    unitName: labelUnit.unitEntry.UNIT_NAME ?? labelUnit.unitEntry.MNF_UNIT,
                    value: barValue,             // bar length: project's own total in ALL mode, single unit's value in specific mode
                    labelValue: labelUnit.value, // label text: the highest unit's own value, or the selected unit's own value
                });

            });
        });

        return rows.sort((a, b) => b.value - a.value);

    }, [groups, selectedFY, selectedUnit]);

    return {
        loading,
        error,
        rankedRows,
        financialYearOptions,
    };
};
